/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 *
 * Standstill page: shown when unauthenticated user tries to view job details.
 * IMPORTANT: Do NOT add timer, countdown, or auto-redirect to this page.
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus,
    faBriefcase,
    faFileAlt,
    faRocket,
    faArrowLeft,
    faSignInAlt
} from '@fortawesome/free-solid-svg-icons';
import AksharLogo from '../assets/FINAL LOGO AK.png';
import '../styles/SignUpToViewJob.css';

const SignUpToViewJob = () => {
    const navigate = useNavigate();
    const { jobId } = useParams();

    const handleSignUp = () => {
        const signupPath = '/signup';
        const redirectUrl = jobId ? `/job-details/${jobId}` : '/jobseeker-dashboard';
        navigate(signupPath, { state: { redirectAfterSignup: redirectUrl } });
    };

    const handleLogin = () => {
        const loginPath = jobId ? `/login?redirect=${encodeURIComponent(`/job-details/${jobId}`)}` : '/login';
        navigate(loginPath);
    };

    const handleBackToHome = () => {
        navigate('/');
    };

    return (
        <div className="signup-to-view-job">
            <div className="signup-to-view-job-header">
                <div className="header-branding">
                    <img src={AksharLogo} alt="AksharJobs logo" className="header-logo" />
                    <div className="branding-text">
                        <span className="branding-name">AksharJobs</span>
                        <span className="branding-tagline">CONNECT | DISCOVER | ELEVATE</span>
                    </div>
                </div>
                <button
                    type="button"
                    className="back-home-btn"
                    onClick={handleBackToHome}
                >
                    <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
                </button>
            </div>

            <div className="signup-to-view-job-content">
                <div className="signup-card">
                    <div className="signup-icon-wrapper">
                        <FontAwesomeIcon icon={faBriefcase} className="signup-icon" />
                    </div>
                    <h1 className="signup-title">Create an Account to View & Apply</h1>
                    <p className="signup-subtitle">
                        To view full job details and apply for this opportunity, you need a jobseeker account.
                        It's free and takes just a few minutes.
                    </p>

                    <p className="process-steps-heading">Steps to get started:</p>
                    <div className="process-steps" aria-label="Process steps (no timer)">
                        <div className="process-step">
                            <div className="step-number" aria-label="Step 1">1</div>
                            <div className="step-content">
                                <FontAwesomeIcon icon={faUserPlus} className="step-icon" />
                                <span>Create your free jobseeker account</span>
                            </div>
                        </div>
                        <div className="process-step">
                            <div className="step-number" aria-label="Step 2">2</div>
                            <div className="step-content">
                                <FontAwesomeIcon icon={faFileAlt} className="step-icon" />
                                <span>Complete your profile (optional but recommended)</span>
                            </div>
                        </div>
                        <div className="process-step">
                            <div className="step-number" aria-label="Step 3">3</div>
                            <div className="step-content">
                                <FontAwesomeIcon icon={faRocket} className="step-icon" />
                                <span>View job details and apply with one click</span>
                            </div>
                        </div>
                    </div>

                    <div className="signup-actions">
                        <button
                            type="button"
                            className="btn-signup"
                            onClick={handleSignUp}
                        >
                            <FontAwesomeIcon icon={faUserPlus} />
                            Sign Up
                        </button>
                        <p className="already-have-account">
                            Already have an account?{' '}
                            <button
                                type="button"
                                className="link-login"
                                onClick={handleLogin}
                            >
                                <FontAwesomeIcon icon={faSignInAlt} /> Sign In
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignUpToViewJob;
